"use client"
import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { updateStudent, fetchStudents } from "../store/slices/studentSlice"
import { ITI_TRADES, POLY_TRADES } from "../data/trades"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Grid,
  Box,
  Typography,
  Divider,
  Paper,
} from "@mui/material"

const EditStudent = ({ student, setEditingStudent }) => {
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.students)
  const [formData, setFormData] = useState({})
  const [profileImage, setProfileImage] = useState(null)

  const handleFileChange = (e) => {
    setProfileImage(e.target.files[0])
  }

  useEffect(() => {
    setFormData({
      ...student,
      name: student.name || "",
      email: student.email || "",
      password: student.password || "",
      council: student.council || "",
      permanent_block: student.block || "",
      permanent_district: student.district || "",
      permanent_pin_code: student.pin_code || "",
      permanent_state: student.state || "",
      correspondence_block: student.correspondence_block || "",
      correspondence_district: student.correspondence_district || "",
      correspondence_pincode: student.correspondence_pincode || "",
      correspondence_state: student.correspondence_state || "",
      semester_or_year: student.semester_or_year || "",
      sub_category: student.sub_category || "",
      caste_cert_no: student.caste_cert_no || "",
      driving_license_no: student.driving_license_no || "",
      medical_fitness_cert_no: student.medical_fitness_cert_no || "",
      tc_serial_no: student.tc_serial_no || "",
      anti_ragging_reg_no: student.anti_ragging_reg_no || "",
      highschool_board_or_university: student.highschool_board_or_university || "",
      highschool_roll_no: student.highschool_roll_no || "",
      highschool_passing_year: student.highschool_passing_year || "",
      highschool_percent_marks: student.highschool_percent_marks || "",
      highschool_max_marks: student.highschool_max_marks || "",
      graduation_board_or_university: student.graduation_board_or_university || "",
      graduation_roll_no: student.graduation_roll_no || "",
      graduation_passing_year: student.graduation_passing_year || "",
      graduation_percent_marks: student.graduation_percent_marks || "",
      graduation_max_marks: student.graduation_max_marks || "",
      international_board_or_university: student.international_board_or_university || "",
      international_roll_no: student.international_roll_no || "",
      international_passing_year: student.international_passing_year || "",
      international_percent_marks: student.international_percent_marks || "",
      international_max_marks: student.international_max_marks || "",
      jeecup_roll_no: student.jeecup_roll_no || "",
      jeecup_amount: student.jeecup_amount || "",
      jeecup_date: student.jeecup_date ? student.jeecup_date.split("T")[0] : "",
      jeecup_transaction_no: student.jeecup_transaction_no || "",
      institute_amount: student.institute_amount || "",
      institute_date: student.institute_date ? student.institute_date.split("T")[0] : "",
      institute_transaction_no: student.institute_transaction_no || "",
      applied_for_hostel: student.applied_for_hostel || "",
      admission_status: student.admission_status || "",
      scholarship: student.scholarship || "No",
      // Added read-only display fields
      department_name: student.department_name || "",
    })
  }, [student])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value === "null" ? null : value,
    })
  }

  const handleUpdate = (e) => {
    e.preventDefault()
    console.log("[v1] Form submission started")
    const mappedFormData = {
      ...formData,
      block: formData.permanent_block,
      district: formData.permanent_district,
      pin_code: formData.permanent_pin_code,
      state: formData.permanent_state,
      correspondence_block: formData.correspondence_block,
      correspondence_district: formData.correspondence_district,
      correspondence_pincode: formData.correspondence_pincode,
      correspondence_state: formData.correspondence_state,
    }
    delete mappedFormData.permanent_block
    delete mappedFormData.permanent_district
    delete mappedFormData.permanent_pin_code
    delete mappedFormData.permanent_state
    delete mappedFormData.enrollment_date
    delete mappedFormData.verification_status
    delete mappedFormData.course_selection_status
    delete mappedFormData.department_id
    // Exclude display-only fields returned by JOINs; backend schema does not allow them
    delete mappedFormData.department_name
    // Remove council as it's not part of the student model for update
    delete mappedFormData.council

    const cleanFormData = Object.fromEntries(
      Object.entries(mappedFormData).filter(([key, value]) => value !== "" && value !== null && value !== undefined),
    )
    console.log("[v1] Clean form data:", cleanFormData)
    const formDataToSend = new FormData()
    Object.keys(cleanFormData).forEach((key) => {
      formDataToSend.append(key, cleanFormData[key])
    })
    if (profileImage) {
      formDataToSend.append("profile_image", profileImage)
      console.log("[v1] Profile image attached:", profileImage.name)
    }
    dispatch(updateStudent({ id: student.student_id, data: formDataToSend }))
      .unwrap()
      .then((result) => {
        console.log("[v1] Student update completed successfully:", result)
        alert("Student updated successfully!")
        setEditingStudent(null)
        setFormData({})
        setProfileImage(null)
        dispatch(fetchStudents())
      })
      .catch((error) => {
        console.error("[v1] Student update failed:", error)
        alert(`Failed to update student: ${error}`)
      })
  }

  const trades = formData.branch_name === "ITI" ? ITI_TRADES : formData.branch_name === "Polytechnic" ? POLY_TRADES : []

  return (
    <Dialog open={true} onClose={() => setEditingStudent(null)} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          fontFamily: "'Times New Roman', Times, serif",
          color: "#333",
          fontSize: "1.5rem",
          p: 2,
          textAlign: "center",
        }}
      >
        Edit Student
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <form onSubmit={handleUpdate}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            {/* Basic Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Council"
                    name="council"
                    fullWidth
                    required
                    disabled
                    variant="outlined"
                    value={formData.council || ""}
                    onChange={(e) => {
                      const val = e.target.value
                      setFormData({
                        ...formData,
                        council: val,
                        // reset downstream selections like in Add flow
                        branch_name: "",
                        trade: "",
                      })
                    }}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "10px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                  >
                    <MenuItem value="">Select Council</MenuItem>
                    <MenuItem value="SCVT">SCVT</MenuItem>
                    <MenuItem value="NCVT">NCVT</MenuItem>
                  </TextField>
                </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                                      <TextField
                                        label="Email"
                                        name="email"
                                        type="email"
                                        required
                                        fullWidth
                                        variant="outlined"
                                        value={formData.email || ""}
                                        onChange={handleInputChange}
                                        sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                      <TextField
                                        label="Password"
                                        name="password"
                                        type="password"
                                        required
                                        fullWidth
                                        variant="outlined"
                                        value={formData.password || ""}
                                        onChange={handleInputChange}
                                        sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                                      />
                                    </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Branch Name"
                    name="branch_name"
                    required
                    fullWidth
                    variant="outlined"
                    value={formData.branch_name || ""}
                    onChange={handleInputChange}
                    disabled
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "10px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                    helperText={!formData.council ? "Select Council first" : ""}
                  >
                    <MenuItem value="">Select Branch</MenuItem>
                    <MenuItem value="ITI">ITI</MenuItem>
                    <MenuItem value="Polytechnic">Polytechnic</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Trade"
                    name="trade"
                    required
                    fullWidth
                    variant="outlined"
                    value={formData.trade || ""}
                    onChange={(e) => {
                      const value = e.target.value
                      const list =
                        formData.branch_name === "ITI"
                          ? ITI_TRADES
                          : formData.branch_name === "Polytechnic"
                            ? POLY_TRADES
                            : []
                      const selected = list.find((t) => t.name === value)
                      setFormData((prev) => ({
                        ...prev,
                        trade: value,
                        ...(formData.branch_name === "ITI" && selected
                          ? {
                              durationYears: String(selected.durationYears),
                              isTechnical: !!selected.isTechnical,
                            }
                          : {}),
                      }))
                    }}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "10px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                  >
                    <MenuItem value="">Select Trade</MenuItem>
                    {trades.map((trade) => (
                      <MenuItem key={trade.name} value={trade.name}>
                        {trade.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Year"
                    name="semester_or_year"
                    fullWidth
                    variant="outlined"
                    value={formData.semester_or_year || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                {/* New read-only Department field */}

                {/* New read-only Assigned Faculty field */}
              </Grid>
            </Box>

            {/* Personal Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Personal Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Student Name"
                    name="name"
                    fullWidth
                    required
                    variant="outlined"
                    value={formData.name || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                {/* existing fields follow */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Date of Birth"
                    name="dob"
                    type="date"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={formData.dob ? formData.dob.split("T")[0] : ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Gender"
                    name="gender"
                    fullWidth
                    variant="outlined"
                    value={formData.gender || ""}
                    onChange={handleInputChange}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "10px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                  >
                    <MenuItem value="">Select Gender</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Father's Name"
                    name="fathers_name"
                    fullWidth
                    variant="outlined"
                    value={formData.fathers_name || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Mother's Name"
                    name="mothers_name"
                    fullWidth
                    variant="outlined"
                    value={formData.mothers_name || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Address Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Address Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", mb: 1 }}
                  >
                    Permanent Address
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Block"
                    name="permanent_block"
                    fullWidth
                    variant="outlined"
                    value={formData.permanent_block || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="District"
                    name="permanent_district"
                    fullWidth
                    variant="outlined"
                    value={formData.permanent_district || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Pin Code"
                    name="permanent_pin_code"
                    fullWidth
                    variant="outlined"
                    value={formData.permanent_pin_code || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="State"
                    name="permanent_state"
                    fullWidth
                    variant="outlined"
                    value={formData.permanent_state || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", mb: 1, mt: 2 }}
                  >
                    Correspondence Address
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Block"
                    name="correspondence_block"
                    fullWidth
                    variant="outlined"
                    value={formData.correspondence_block || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="District"
                    name="correspondence_district"
                    fullWidth
                    variant="outlined"
                    value={formData.correspondence_district || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Pin Code"
                    name="correspondence_pincode"
                    fullWidth
                    variant="outlined"
                    value={formData.correspondence_pincode || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="State"
                    name="correspondence_state"
                    fullWidth
                    variant="outlined"
                    value={formData.correspondence_state || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Document Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Document Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Aadhar Card Number"
                    name="aadhar_card_no"
                    pattern="[0-9]{12}"
                    maxLength="12"
                    placeholder="12-digit Aadhar number"
                    fullWidth
                    variant="outlined"
                    value={formData.aadhar_card_no || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Blood Group"
                    name="blood_group"
                    fullWidth
                    variant="outlined"
                    value={formData.blood_group || ""}
                    onChange={handleInputChange}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "10px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                  >
                    <MenuItem value="">Select Blood Group</MenuItem>
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Caste"
                    name="category"
                    fullWidth
                    variant="outlined"
                    value={formData.category || ""}
                    onChange={handleInputChange}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "10px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                  >
                    <MenuItem value="">Select Category</MenuItem>
                    <MenuItem value="General">General</MenuItem>
                    <MenuItem value="OBC">OBC</MenuItem>
                    <MenuItem value="SC">SC</MenuItem>
                    <MenuItem value="ST">ST</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Sub Category"
                    name="sub_category"
                    fullWidth
                    variant="outlined"
                    value={formData.sub_category || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Caste Certificate No"
                    name="caste_cert_no"
                    fullWidth
                    variant="outlined"
                    value={formData.caste_cert_no || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Driving License No"
                    name="driving_license_no"
                    fullWidth
                    variant="outlined"
                    value={formData.driving_license_no || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Medical Fitness Certificate No"
                    name="medical_fitness_cert_no"
                    fullWidth
                    variant="outlined"
                    value={formData.medical_fitness_cert_no || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="TC Serial No"
                    name="tc_serial_no"
                    fullWidth
                    variant="outlined"
                    value={formData.tc_serial_no || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Anti Ragging Registration No"
                    name="anti_ragging_reg_no"
                    fullWidth
                    variant="outlined"
                    value={formData.anti_ragging_reg_no || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Academic Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Academic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", mb: 1 }}
                  >
                    High School
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Board/University"
                    name="highschool_board_or_university"
                    fullWidth
                    variant="outlined"
                    value={formData.highschool_board_or_university || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Roll No"
                    name="highschool_roll_no"
                    fullWidth
                    variant="outlined"
                    value={formData.highschool_roll_no || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Passing Year"
                    name="highschool_passing_year"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={formData.highschool_passing_year || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Percent Marks"
                    name="highschool_percent_marks"
                    type="number"
                    step="0.01"
                    fullWidth
                    variant="outlined"
                    value={formData.highschool_percent_marks || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Max Marks"
                    name="highschool_max_marks"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={formData.highschool_max_marks || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", mb: 1, mt: 2 }}
                  >
                    Graduation
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Board/University"
                    name="graduation_board_or_university"
                    fullWidth
                    variant="outlined"
                    value={formData.graduation_board_or_university || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Roll No"
                    name="graduation_roll_no"
                    fullWidth
                    variant="outlined"
                    value={formData.graduation_roll_no || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Passing Year"
                    name="graduation_passing_year"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={formData.graduation_passing_year || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Percent Marks"
                    name="graduation_percent_marks"
                    type="number"
                    step="0.01"
                    fullWidth
                    variant="outlined"
                    value={formData.graduation_percent_marks || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Max Marks"
                    name="graduation_max_marks"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={formData.graduation_max_marks || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", mb: 1, mt: 2 }}
                  >
                    International
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Board/University"
                    name="international_board_or_university"
                    fullWidth
                    variant="outlined"
                    value={formData.international_board_or_university || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Roll No"
                    name="international_roll_no"
                    fullWidth
                    variant="outlined"
                    value={formData.international_roll_no || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Passing Year"
                    name="international_passing_year"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={formData.international_passing_year || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Percent Marks"
                    name="international_percent_marks"
                    type="number"
                    step="0.01"
                    fullWidth
                    variant="outlined"
                    value={formData.international_percent_marks || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Max Marks"
                    name="international_max_marks"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={formData.international_max_marks || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* JEECUP Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                JEECUP Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Roll No"
                    name="jeecup_roll_no"
                    fullWidth
                    variant="outlined"
                    value={formData.jeecup_roll_no || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Amount"
                    name="jeecup_amount"
                    type="number"
                    step="0.01"
                    fullWidth
                    variant="outlined"
                    value={formData.jeecup_amount || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Date"
                    name="jeecup_date"
                    type="date"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={formData.jeecup_date || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Transaction No"
                    name="jeecup_transaction_no"
                    fullWidth
                    variant="outlined"
                    value={formData.jeecup_transaction_no || ""}
                    onChange={handleInputChange}
                    sx={{ fontFamily: "'Times New Roman', Times, serif" }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Institute Information Section */}

            {/* Other Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#1976d2", mb: 2 }}>
                Other Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Applied for Hostel"
                    name="applied_for_hostel"
                    fullWidth
                    variant="outlined"
                    value={formData.applied_for_hostel || ""}
                    onChange={handleInputChange}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "10px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                </Grid>
                {/* New field for scholarship */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Scholarship"
                    name="scholarship"
                    fullWidth
                    variant="outlined"
                    value={formData.scholarship || "No"}
                    onChange={handleInputChange}
                    sx={{
                      fontFamily: "'Times New Roman', Times, serif",
                      "& .MuiSelect-select": { padding: "10px", minWidth: "150px" },
                      "& .MuiMenuItem-root": { fontFamily: "'Times New Roman', Times, serif", whiteSpace: "normal" },
                    }}
                  >
                    <MenuItem value="No">No</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#333", mb: 1 }}
                    >
                      Profile Photo
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{
                        fontFamily: "'Times New Roman', Times, serif",
                        borderColor: "#1976d2",
                        color: "#1976d2",
                        "&:hover": { borderColor: "#115293", color: "#115293" },
                      }}
                    >
                      Upload Photo
                      <input type="file" name="profile_image" accept="image/*" hidden onChange={handleFileChange} />
                    </Button>
                    {profileImage && (
                      <Box sx={{ mt: 2, textAlign: "center" }}>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", display: "block", mb: 1 }}
                        >
                          Selected: {profileImage.name}
                        </Typography>
                        <img
                          src={URL.createObjectURL(profileImage) || "/placeholder.svg"}
                          alt="Preview"
                          style={{
                            width: 120,
                            height: 120,
                            objectFit: "cover",
                            border: "2px solid #1976d2",
                            borderRadius: "50%",
                          }}
                        />
                      </Box>
                    )}
                    {student.profile_image && !profileImage && (
                      <Box sx={{ mt: 2, textAlign: "center" }}>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "'Times New Roman', Times, serif", color: "#555", display: "block", mb: 1 }}
                        >
                          Current: {student.profile_image}
                        </Typography>
                        <img
                          src={student.profile_image || "/placeholder.svg"}
                          alt="Current Profile"
                          style={{
                            width: 120,
                            height: 120,
                            objectFit: "cover",
                            border: "2px solid #1976d2",
                            borderRadius: "50%",
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: "center" }}>
        <Button
          onClick={() => {
            setEditingStudent(null)
            setFormData({})
            setProfileImage(null)
          }}
          sx={{
            textTransform: "none",
            fontFamily: "'Times New Roman', Times, serif",
            color: "#f44336",
            border: "1px solid #f44336",
            px: 3,
            py: 1,
            mr: 2,
            "&:hover": { bgcolor: "#f44336", color: "#fff" },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          onClick={handleUpdate}
          disabled={loading}
          sx={{
            textTransform: "none",
            fontFamily: "'Times New Roman', Times, serif",
            bgcolor: "#1976d2",
            color: "#fff",
            px: 3,
            py: 1,
            "&:hover": { bgcolor: "#115293" },
            "&:disabled": { bgcolor: "#ccc", color: "#fff" },
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Update Student"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditStudent
